import { db } from "@/stores/indexed-db";
import { useSetTask } from "tinytick/ui-react";
import { useNetworkState } from "@uidotdev/usehooks";
import { useAuthValue } from "@/stores/auth-store";
import { useUserValue } from "@/stores/user-store";
import { toast } from "sonner";
import { client } from "@/utils/orpc";
import { isErrorResponse } from "@shamba-odk/types";

export const OutgrowerFarmerStore = () => {
    const accessToken = useAuthValue("accessToken");
    const user = useUserValue("name") === "Administrator" ? useUserValue("name") : useUserValue("email");
    const network = useNetworkState();

    useSetTask(
        "syncOutgrowerFarmers",
        async () => {
            if (!network.online || !accessToken || !user) {
                return;
            }

            await syncOutgrowerFarmers(accessToken, user);
        },
        [accessToken],
        "sync",
        {
            retryDelay: 5000,
            maxRetries: 4
        }
    );

    return null;
}

export const syncOutgrowerFarmers = async (token: string, user: string) => {
    try {
        // Use a more efficient filter approach with the indexed status field
        const unsyncedFarmers = await db.outgrower_farmers
            .where("status")
            .anyOf(["unsynced", "partial-sync"])
            .toArray();

        if (unsyncedFarmers.length === 0) {
            return;
        }

        let farmerIndex = 0;
        for (const farmer of unsyncedFarmers) {
            farmerIndex++;
            const doc = {
                ...farmer,
                custom_lga: farmer.lga,
                field_officer: user,
                passport_photo: undefined,
                id_photo: undefined,
                bvn_photo: undefined,
                signature: undefined,
                status: undefined,
                creation: undefined,
                modified: undefined,
            }

            toast.info(`Syncing Outgrower Farmer ${farmer.phone_number} (${farmerIndex}/${unsyncedFarmers.length})`);

            // Handle differently based on sync status
            let docName: string = "";

            try {
                if (farmer.status === "partial-sync") {
                    // For partially synced farmers, update the existing document
                    // Use phone_number as the primary key
                    const updateRes = await client.frappe.updateDoc({
                        accessToken: token,
                        doctype: "Outgrower Farmer",
                        name: farmer.phone_number as string, // Use phone_number as the primary key
                        doc: doc,
                    });

                    if (isErrorResponse(updateRes)) {
                        throw new Error(updateRes.error.message || 'Failed to update outgrower farmer record');
                    }

                    docName = farmer.phone_number as string;
                } else {
                    // For unsynced farmers, create a new document
                    const createRes = await client.frappe.createDoc({
                        accessToken: token,
                        doctype: "Outgrower Farmer",
                        doc: doc,
                    });

                    if (isErrorResponse(createRes)) {
                        throw new Error(createRes.error.message || 'Failed to create outgrower farmer record');
                    }

                    docName = createRes.data.data.name;
                    // Update local DB to mark as partially synced
                    await db.outgrower_farmers.update(farmer.phone_number, { status: "partial-sync" });
                }

                // Helper function to upload a file
                const uploadFile = async (file: File, fieldname: string): Promise<string | undefined> => {
                    try {
                        const uploadResponse = await client.frappe.uploadFile({
                            accessToken: token,
                            file: file,
                            docname: docName,
                            doctype: "Outgrower Farmer",
                            fieldname: fieldname,
                            file_name: `${docName}_${fieldname}`,
                        });

                        if (isErrorResponse(uploadResponse)) {
                            throw new Error(uploadResponse.error.message || `Failed to upload ${fieldname}`);
                        }

                        // Extract the file_url from the upload response
                        return uploadResponse.data.message?.file_url;
                    } catch (error) {
                        console.error(`[outgrower-farmer] Error Uploading ${fieldname}:`, error);
                        // Re-throw the error so it's caught by the outer try-catch in the syncOutgrowerFarmers loop
                        throw error;
                    }
                };

                // Prepare to store file URLs for document update
                const fileUpdates: Record<string, string> = {};

                // Type guard function to check if a value is a FileList
                const isFileList = (value: any): value is FileList => {
                    return value && typeof value !== "string" &&
                        typeof value.length === "number" &&
                        typeof value.item === "function";
                };

                // Helper function to process file uploads
                const processFileUpload = async (
                    fileValue: FileList | string | undefined,
                    fieldName: 'passport_photo' | 'id_photo' | 'bvn_photo' | 'signature'
                ) => {
                    if (fileValue && isFileList(fileValue) && fileValue.length > 0) {
                        const fileUrl = await uploadFile(fileValue[0], fieldName);
                        if (fileUrl) fileUpdates[fieldName] = fileUrl;
                    }
                };

                // Process all file uploads
                await processFileUpload(farmer.passport_photo, 'passport_photo');
                await processFileUpload(farmer.id_photo, 'id_photo');
                await processFileUpload(farmer.bvn_photo, 'bvn_photo');
                await processFileUpload(farmer.signature, 'signature');

                // Update the document with file URLs if any were uploaded
                if (Object.keys(fileUpdates).length > 0) {
                    const fileUpdateRes = await client.frappe.updateDoc({
                        accessToken: token,
                        doctype: "Outgrower Farmer",
                        name: docName,
                        doc: fileUpdates
                    });

                    if (isErrorResponse(fileUpdateRes)) {
                        throw new Error(fileUpdateRes.error.message || 'Failed to update file references');
                    }
                }

                // Mark as synced
                // Create update object with only status and modified date
                const dbUpdateObj: Record<string, any> = {
                    status: "synced",
                    modified: new Date().toISOString()
                };

                // Only include photo fields if they were updated
                if (fileUpdates.passport_photo) dbUpdateObj.passport_photo = fileUpdates.passport_photo;
                if (fileUpdates.id_photo) dbUpdateObj.id_photo = fileUpdates.id_photo;
                if (fileUpdates.bvn_photo) dbUpdateObj.bvn_photo = fileUpdates.bvn_photo;
                if (fileUpdates.signature) dbUpdateObj.signature = fileUpdates.signature;

                // Update the local database
                await db.outgrower_farmers.update(farmer.phone_number, dbUpdateObj);

                toast.success(`Successfully synced Outgrower Farmer ${farmer.phone_number} (${farmerIndex}/${unsyncedFarmers.length})`);
            } catch (error) {
                console.error(`[outgrower-farmer] Error Processing Outgrower Farmer ${farmer.phone_number}:`, error);

                // Show a more specific error message to the user
                let errorMessage = `Failed to sync outgrower farmer ${farmer.phone_number}`;
                if (error instanceof Error) {
                    errorMessage = error.message;
                }
                toast.error(`Error: ${errorMessage}. Continuing with next farmer.`);
                // Do NOT re-throw error, to allow the loop to continue with the next farmer.
            }
        }

    } catch (error) {
        console.error("[outgrower-farmer] Error Syncing Outgrower Farmers:", error);
        toast.error("Error Syncing Outgrower Farmers");
    }
}
